'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';
import LocationBrowser, { LocationSelection, formatLocationSelection } from '@/components/LocationBrowser';
import InteractiveMap, { MapMarker } from '@/components/InteractiveMap';

interface Package {
  id: string;
  slug: string;
  name: string;
  duration: string;
  price: number;
  rating: number;
  reviews_count: number;
  difficulty: string;
  image_url: string;
  alt_text: string;
  category: string;
  region: string;
}

const fallbackPackages: Package[] = [
  { id: '1', slug: 'everest-base-camp', name: 'Everest Base Camp Trek', duration: '14 Days', price: 85000, rating: 4.9, reviews_count: 248, difficulty: 'Challenging', image_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', alt_text: 'Trekkers at Everest Base Camp with Khumbu Icefall', category: 'Trekking', region: 'Koshi' },
  { id: '2', slug: 'annapurna-circuit', name: 'Annapurna Circuit Trek', duration: '12 Days', price: 65000, rating: 4.8, reviews_count: 186, difficulty: 'Moderate', image_url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=80', alt_text: 'Annapurna Circuit trail with mountain views', category: 'Trekking', region: 'Gandaki' },
  { id: '3', slug: 'pokhara-luxury', name: 'Pokhara Luxury Retreat', duration: '5 Days', price: 45000, rating: 4.9, reviews_count: 312, difficulty: 'Easy', image_url: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', alt_text: 'Luxury resort at Phewa Lake Pokhara', category: 'Luxury', region: 'Gandaki' },
  { id: '4', slug: 'chitwan-wildlife', name: 'Chitwan Wildlife Safari', duration: '4 Days', price: 32000, rating: 4.7, reviews_count: 145, difficulty: 'Easy', image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', alt_text: 'Wildlife safari in Chitwan National Park', category: 'Wildlife', region: 'Bagmati' },
];

const categories = ['All', 'Trekking', 'Luxury', 'Wildlife', 'Adventure', 'Religious', 'Cultural'];
const difficulties = ['All', 'Easy', 'Moderate', 'Challenging'];

const difficultyColor: Record<string, string> = {
  Easy: 'badge-verified',
  Moderate: 'badge-moderate',
  Challenging: 'badge-challenging',
};

const PACKAGE_COORDS: Record<string, { lat: number; lng: number }> = {
  'everest': { lat: 27.9881, lng: 86.9250 },
  'annapurna': { lat: 28.5967, lng: 83.8200 },
  'pokhara': { lat: 28.2096, lng: 83.9856 },
  'chitwan': { lat: 27.5291, lng: 84.3542 },
  'mustang': { lat: 28.9965, lng: 83.8500 },
  'langtang': { lat: 28.2140, lng: 85.5140 },
  'manaslu': { lat: 28.5500, lng: 84.5590 },
  'kanchenjunga': { lat: 27.7025, lng: 88.1475 },
};

export default function PackagesPage() {
  const [allPackages, setAllPackages] = useState<Package[]>(fallbackPackages);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [difficulty, setDifficulty] = useState('All');
  const [locationSel, setLocationSel] = useState<LocationSelection>({});
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('packages')
      .select('*')
      .order('rating', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          setAllPackages(data);
        }
        setLoading(false);
      });
  }, []);

  const filtered = allPackages?.filter((p) => {
    if (category !== 'All' && p?.category !== category) return false;
    if (difficulty !== 'All' && p?.difficulty !== difficulty) return false;
    // Location filter: match province/region if selected
    if (locationSel.province && p?.region && p.region !== locationSel.province.name) return false;
    return true;
  });

  const locationLabel = formatLocationSelection(locationSel);

  const mapMarkers: MapMarker[] = filtered.map(pkg => {
    const key = Object.keys(PACKAGE_COORDS).find(k => pkg.name?.toLowerCase().includes(k) || pkg.slug?.toLowerCase().includes(k));
    const coords = key ? PACKAGE_COORDS[key] : { lat: 28.3949 + (Math.random() - 0.5) * 2, lng: 84.124 + (Math.random() - 0.5) * 2 };
    return {
      id: pkg.id,
      lat: coords.lat,
      lng: coords.lng,
      title: pkg.name,
      description: `${pkg.duration} · ${pkg.difficulty}`,
      category: 'trekking',
      price: pkg.price ? `NPR ${pkg.price.toLocaleString()}` : undefined,
      rating: pkg.rating,
      image: pkg.image_url,
      link: `/packages/${pkg.slug}`,
    };
  });

  return (
    <main className="min-h-screen bg-navy">
      <Header />

      <div className="pt-24 pb-10 px-4" style={{ background: 'linear-gradient(180deg, #0E3570 0%, #0B2D5B 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="section-eyebrow mb-2">Curated Nepal Experiences</div>
          <h1 className="heading-display text-3xl md:text-5xl text-snow mb-3">
            Tour <span className="italic text-gold">Packages</span>
          </h1>
          <p className="text-snow/60 text-sm max-w-xl">
            Handcrafted itineraries for every type of traveller — from luxury retreats to high-altitude adventures.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Location Browser */}
        <div className="yn-card rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-gold text-sm">📍</span>
            <h3 className="text-snow text-sm font-semibold">Filter by Location</h3>
            {locationLabel && (
              <span className="ml-auto text-xs text-gold/80 bg-gold/10 px-2 py-0.5 rounded-full truncate max-w-xs">{locationLabel}</span>
            )}
            {locationLabel && (
              <button onClick={() => setLocationSel({})} className="text-snow/40 hover:text-snow/70 text-xs transition-colors ml-1">✕ Clear</button>
            )}
          </div>
          <LocationBrowser
            nepalOnly
            levels={['province', 'district', 'city']}
            value={locationSel}
            onChange={setLocationSel}
            compact
          />
        </div>

        {/* Category & Difficulty Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {categories?.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${category === c ? 'bg-gold text-navy border-gold font-semibold' : 'border-white/15 text-snow/60 hover:border-gold/40'}`}>{c}</button>
            ))}
          </div>
          <select value={difficulty} onChange={(e) => setDifficulty(e?.target?.value)} className="yn-select px-4 py-2 rounded-full text-sm ml-auto">
            {difficulties?.map((d) => <option key={d}>{d}</option>)}
          </select>
          <button
            onClick={() => setShowMap(m => !m)}
            className={`flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-medium transition-all ${showMap ? 'bg-gold/20 border-gold/50 text-gold' : 'border-white/15 text-snow/60 hover:border-gold/40'}`}
          >
            🗺️ {showMap ? 'Hide Map' : 'Map View'}
          </button>
        </div>

        {/* Interactive Map */}
        {showMap && (
          <div className="yn-card rounded-2xl p-4 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-gold text-lg">🗺️</span>
              <h2 className="text-snow font-bold text-lg">Packages Map</h2>
              <span className="text-snow/40 text-xs ml-auto">Satellite · Traffic · Route Planning · Nearby Search</span>
            </div>
            <InteractiveMap
              markers={mapMarkers}
              centerLat={28.3949}
              centerLng={84.124}
              zoom={7}
              height="460px"
              showControls
              showNearbySearch
              showRoutePlanning
            />
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} className="yn-card rounded-2xl overflow-hidden animate-pulse">
                <div className="h-52 bg-white/5" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-white/5 rounded w-3/4" />
                  <div className="h-3 bg-white/5 rounded w-1/2" />
                  <div className="h-6 bg-white/5 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered?.map((pkg) => (
              <Link key={pkg?.id} href={`/packages/${pkg?.slug}`} className="yn-card rounded-2xl overflow-hidden group block">
                <div className="relative h-52 overflow-hidden">
                  <AppImage src={pkg?.image_url} alt={pkg?.alt_text} fill className="object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="badge-gold text-[10px] font-semibold px-2 py-1 rounded-full">{pkg?.category}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className={`${difficultyColor[pkg?.difficulty] || 'badge-moderate'} text-[10px] font-medium px-2 py-1 rounded-full`}>{pkg?.difficulty}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-snow font-semibold text-base mb-2">{pkg?.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-snow/50 mb-3">
                    <span>{pkg?.duration}</span>
                    <span>·</span>
                    <div className="flex items-center gap-1">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="#D4A017">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      <span className="text-gold font-semibold">{pkg?.rating}</span>
                      <span>({pkg?.reviews_count})</span>
                    </div>
                    {pkg?.region && <span className="text-snow/30">· {pkg.region}</span>}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div>
                      <span className="text-gold font-bold text-lg">Rs. {pkg?.price?.toLocaleString()}</span>
                      <span className="text-snow/40 text-xs"> / person</span>
                    </div>
                    <span className="btn-gold text-xs px-4 py-2 rounded-lg">Book Now</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && filtered?.length === 0 && (
          <div className="text-center py-20">
            <div className="text-4xl mb-4">📦</div>
            <h3 className="text-snow/60 font-semibold mb-2">No packages found</h3>
            <p className="text-snow/30 text-sm">Try adjusting your filters or location</p>
            <button onClick={() => { setCategory('All'); setDifficulty('All'); setLocationSel({}); }}
              className="mt-4 text-gold text-sm hover:underline">Clear all filters</button>
          </div>
        )}
      </div>

      <Footer />
    </main>
  );
}
